<template>
  <div v-if="!loading">
    <b-row>
      <b-col class="d-flex justify-content-between pb-1 align-items-center">
        <h1 class="font-weight-bolder">
          {{ $t('purchaseOrder') }}
        </h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-badge
          class="mr-1"
          :variant="
            $helpers.quoteStatusColor(purchaseOrders[0].quote.quote_status)
          "
        >
          {{ $helpers.quoteStatusName(purchaseOrders[0].quote.quote_status) }}
        </b-badge>
        <b-badge
          :variant="
            $helpers.paymentStatusColor(purchaseOrders[0].quote.payment_status)
          "
        >
          {{
            $helpers.paymentStatusName(purchaseOrders[0].quote.payment_status)
          }}
        </b-badge>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col>
        <checkout
          :quote="purchaseOrderQuote"
          :project="checkoutProject"
          :items="purchaseOrdersItems"
          :address="purchaseOrderAddress"
          :tax-info="checkoutProject.tax_information || {}"
          :supplier-data="supplierData"
          :refetch="fetchData"
        />
      </b-col>
    </b-row>
    <b-row v-if="purchaseOrders.length === 0">
      <b-col>
        <h4 class="display-4">
          No hay Cotizaciones 🧟
        </h4>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Checkout from '@/components/invoice/Checkout.vue'

export default {
  components: {
    Checkout,
  },

  data() {
    return {
      loading: false,
      purchaseOrderQuote: {},
      purchaseOrderAddress: {},
      purchaseOrdersItems: [],
      supplierData: [], // Nueva propiedad para almacenar datos del proveedor
      deliveryProject: {},
    }
  },

  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('purchaseOrders', ['purchaseOrders']),
    ...mapGetters('projects', ['project']),

    isSupplierViewer() {
      return ['supplier_admin', 'auctioneer'].includes(this.currentUser?.role_name)
    },

    // El proveedor no puede leer el proyecto, asi que toma el recorte que viene en su propia
    // orden. Ademas evita quedarse con el proyecto que otra pantalla haya dejado en el store,
    // que seria el de un cliente distinto.
    checkoutProject() {
      return this.isSupplierViewer ? this.deliveryProject : this.project
    },
  },

  created() {
    this.fetchData()
  },

  methods: {
    ...mapActions('purchaseOrders', [
      'fetchPurchaseOrders',
      'fetchPurchaseOrder',
    ]),
    ...mapActions('quotes', ['getQuotePayments']),
    ...mapActions('projects', ['fetchProject']),
    ...mapMutations('purchaseOrders', {
      setPurchaseOrder: 'SET_PURCHASE_ORDER',
    }),
    async fetchData() {
      this.loading = true
      try {
        const response = await this.fetchPurchaseOrders({
          quoteId: this.$route.params.quoteId,
        })

        if (!response.data.length) return response

        const projectId = response.data[0].quote.project_id
        const suppliers = []
        let allItems = []

        for (let i = 0; i < response.data.length; i += 1) {
          const items = response.data[i].purchase_order_items.map(order => {
            if (order.selected_supplier_branch_id === null) {
              return {
                ...order,
                supplier: 'No hay proveedor',
              }
            }
            if (!suppliers.includes(order.selected_supplier_branch_id)) {
              suppliers.push(order.selected_supplier_branch_id)
            }
            return {
              ...order,
              supplier: ['admin', 'auctioneer', 'supplier_admin'].includes(
                this.currentUser.role_name,
              )
                ? response.data[i].supplier_branch.name
                : `Proveedor ${suppliers.findIndex(
                  supplierId => supplierId === order.selected_supplier_branch_id,
                ) + 1}`,
            }
          })
          allItems = [...allItems, ...items].filter(
            (item, index, self) => index === self.findIndex(t => t.id === item.id),
          )
        }

        this.purchaseOrdersItems = allItems
        const order = response.data[0]
        if (['auctioneer', 'supplier_admin'].includes(this.currentUser.role_name)) {
          order.iva_total = order.iva
          order.shipping_total = order.iva
          // El folio y la fecha de emision son de la cotizacion; la orden no los trae y la
          // cabecera salia como "Folio de cotizacion #" vacio y "No publicada".
          this.purchaseOrderQuote = {
            ...order,
            folio: order.quote?.folio,
            published_at: order.quote?.published_at,
          }
        } else {
          this.purchaseOrderQuote = order.quote
        }
        this.setPurchaseOrder(order)
        this.purchaseOrderAddress = order.address
        this.supplierData = response.data.map(item => item.supplier_branch)
        this.deliveryProject = order.quote?.delivery_project || {}

        // El proyecto del cliente y sus pagos no son datos del proveedor: la API se los niega
        // con 403 y la pantalla terminaba mostrando dos errores de permisos en cada carga.
        if (!this.isSupplierViewer) {
          await Promise.all([
            this.fetchProject(projectId),
            this.getQuotePayments({ quoteId: this.$route.params.quoteId }),
          ])
        }

        return response
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style></style>
